import { PAGE_SIZES } from "@alm/domain";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import type { RouterOutputs } from "@alm/api-contracts";
import { VISITOR_TYPE_LABELS } from "@alm/validation";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";

import { DataTable } from "@/components/app/patterns/data-table";
import { ListToolbar } from "@/components/app/patterns/list-toolbar";
import { PersonCell } from "@/components/app/patterns/person-cell";
import { RelativeTime } from "@/components/app/patterns/relative-time";
import { SearchField } from "@/components/app/patterns/search-field";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalInt, searchPatch, optionalSearch } from "@/lib/search";
import { guardRoute } from "@/lib/policy/route-guard";
import { useZoneFor } from "@/lib/facility/zone";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { VisitorNav } from "../-components/visitor-nav";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/people/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; page?: number; size?: number } => ({
    q: optionalSearch(search.q),
    page: optionalInt(search.page, 2, 100000),
    size: PAGE_SIZES.find((size) => size === Number(search.size)),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: VisitorPeoplePage,
});

type Person = RouterOutputs["visitors"]["people"]["list"]["rows"][number];

const fullName = (person: Person) => `${person.firstName} ${person.lastName}`;

function columns(zoneFor: (facilityId?: string | null) => string): ColumnDef<Person>[] {
  return [
    {
      id: "person",
      header: "Visitor",
      accessorFn: fullName,
      enableHiding: false,
      enableSorting: false,
      meta: { className: "min-w-56" },
      cell: ({ row: { original: person } }) => (
        <PersonCell
          name={fullName(person)}
          meta={[VISITOR_TYPE_LABELS[person.visitorType], person.company]}
          renderLink={(name) => (
            <Link
              to="/visitors/people/$personId"
              params={{ personId: person.id }}
              className="focus-ring rounded-sm hover:underline"
            >
              {name}
            </Link>
          )}
        />
      ),
    },
    {
      id: "phone",
      header: "Phone",
      accessorFn: (person) => person.phone ?? "",
      enableSorting: false,
      meta: { nowrap: true, priority: 2 },
      cell: ({ row: { original: person } }) =>
        person.phone ? <span className="tabular-nums">{person.phone}</span> : "-",
    },
    {
      id: "visits",
      header: "Visits",
      accessorFn: (person) => person.visitCount,
      enableSorting: false,
      meta: { nowrap: true, align: "right" },
      cell: ({ row: { original: person } }) => (
        <span className="tabular-nums">{person.visitCount}</span>
      ),
    },
    {
      id: "last",
      header: "Last visit",
      accessorFn: (person) => new Date(person.lastVisitAt).getTime(),
      enableSorting: false,
      meta: { nowrap: true },
      cell: ({ row: { original: person } }) => (
        <RelativeTime value={person.lastVisitAt} timeZone={zoneFor(person.facilityId)} />
      ),
    },
    {
      id: "first",
      header: "First visit",
      accessorFn: (person) => new Date(person.firstVisitAt).getTime(),
      enableSorting: false,
      meta: { nowrap: true, priority: 3 },
      cell: ({ row: { original: person } }) => (
        <RelativeTime value={person.firstVisitAt} timeZone={zoneFor(person.facilityId)} />
      ),
    },
  ];
}

/** Everyone who has come through the door, one row each, however many times they came. */
function VisitorPeoplePage() {
  const { facilityId } = useFacility();
  const zoneFor = useZoneFor();
  const { q: needle, page = 1, size } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const refine = (next: Partial<ReturnType<typeof Route.useSearch>>) =>
    patch({ page: undefined, ...next });
  const { text: search, change: setSearch } = useSearchParamText(needle, (q) => refine({ q }));
  const pagination: PaginationState = { pageIndex: page - 1, pageSize: size ?? PAGE_SIZES[0] };
  const setPagination = (next: PaginationState) =>
    next.pageSize !== pagination.pageSize
      ? refine({ size: next.pageSize === PAGE_SIZES[0] ? undefined : next.pageSize })
      : patch({ page: next.pageIndex > 0 ? next.pageIndex + 1 : undefined });
  const list = useQuery({
    ...orpc.visitors.people.list.queryOptions({
      input: {
        facilityId,
        search: needle,
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const total = list.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Visitor history"
        count={list.isSuccess ? total : undefined}
        meta="One row per person. A phone number, or the same name from the same company, ties their visits together."
      />
      <VisitorNav />
      {list.isError ? (
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load the visitors."
            error={list.error}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        </Card>
      ) : (
        <DataTable
          data={list.data?.rows ?? []}
          columns={columns(zoneFor)}
          noun="visitors"
          rowId={(person) => person.id}
          pending={list.isPending}
          server={{ rowCount: total, pagination, onPaginationChange: setPagination }}
          renderToolbar={() => (
            <ListToolbar
              search={
                <SearchField
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by name, company or phone"
                  label="Search visitors"
                />
              }
            />
          )}
          empty={
            <EmptyState
              title={needle ? "Nobody matches that search" : "Nobody has visited yet"}
              description={
                needle
                  ? "Try a last name, a company or the phone number they gave."
                  : "People appear here after their first check-in at the kiosk or the desk."
              }
            />
          }
        />
      )}
    </div>
  );
}
